import { ref, reactive, computed } from 'vue';
import { useAsyncState } from '@vueuse/core';
import { defineStore, acceptHMRUpdate } from 'pinia';
import { jsonapiStore, legacyApi, jsonapiPreservedStore, sortToPreservedJson } from '@dashboardApp/store';
import { useUserStore } from '@dashboardApp/stores/UserStore';
import { findThreadFrom, groupToDatedMessages } from '@dashboardApp/helpers/inbox';

import router from '@dashboardApp/router';

export const useInboxStore = defineStore('InboxStore', () => {
    const store = jsonapiStore();
    const preservedStore = jsonapiPreservedStore();
    const userStore = useUserStore();

    const messages = ref([]);
    const archivedMessages = ref([]);
    const thread = ref([]);
    const threadId = ref(0);
    const initialMessagesLoaded = ref(false);
    const initialArchivedMessagesLoaded = ref(false);

    const hasConversations = computed(() => messages.value.length > 0);
    const hasArchive = computed(() => archivedMessages.value.length > 0);
    const hasMessages = computed(() => messages.value.length > 0 || archivedMessages.value.length);
    const hasThread = computed(() => thread.value.length > 0);
    const datedMessages = computed(() => groupToDatedMessages(thread.value));
    const isArchived = computed(() => {
        return Object.keys(thread.value)
            .reduce((acc, key) => (acc && thread.value[key].archived), true);
    });

    const signatureDefault = computed(() => `Kind Regards,\n${userStore.user?.fullname}`);
    const from = computed(() => findThreadFrom(thread.value, userStore.user.id));

    let messagesPage = 1;
    let archivedPage = 1;
    let threadMessagesPage = 1;

    const { execute: getMessages, isReady: messagesAreReady, isLoading: messagesAreLoading } = useAsyncState(
        getMessagesPromise,
        {},
        { immediate: false }
    );

    function getMessagesPromise(args) {
        const isNext = args?.isNext || false;
        const appendResult = args?.appendResult || true;

        if (isNext && messagesPage < 0) {
            return;
        }

        let page = isNext ? ++messagesPage : 1;

        // Reset
        if (page === 1 && messages.value.length > 0) {
            messagesPage = 1;
            messages.value = [];
        }

        return preservedStore.get(`messages?page[number]=${page}`).then((res) => {
            const sortedRes = sortToPreservedJson(res);
            
            if (appendResult) {
                messages.value = messages.value.concat(sortedRes);
            }

            if (sortedRes.length < 1) {
                messagesPage = -1;
            }
            return res;
        });
    }

    const { execute: getArchivedMessages, isReady: archivedMessagesAreReady, isLoading: archivedMessagesAreLoading } = useAsyncState(
        getArchivedMessagesPromise,
        {},
        { immediate: false }
    );

    function getArchivedMessagesPromise(args) {
        const isNext = args?.isNext || false;
        if (isNext && archivedPage < 0) {
            return;
        }

        let page = isNext ? ++archivedPage : 1;

        // Reset
        if (page === 1 && archivedMessages.value.length > 0) {
            archivedPage = 1;
            archivedMessages.value = [];
        }

        return preservedStore.get(`messages/archived?page[number]=${page}`).then((res) => {
            const sortedRes = sortToPreservedJson(res);
            archivedMessages.value = archivedMessages.value.concat(sortedRes);
            if (sortedRes.length < 1) {
                archivedPage = -1;
            }
            return res;
        });
    }


    const { execute: getThread, isReady: threadIsReady, isLoading: threadIsLoading } = useAsyncState(
        getThreadPromise,
        {},
        { immediate: false }
    );

    function getThreadPromise(args) {
        const id = args?.id || null;
        const archived = args?.archived || 0;
        const isNext = args?.isNext || false;

        if (!id) {
            return;
        }

        if (isNext && threadMessagesPage < 0) {
            return;
        }

        let page = isNext ? ++threadMessagesPage : 1;

        // Reset
        if (page === 1 && thread.value.length > 0) {
            resetThread();
        }

        threadId.value = id;
        
        return preservedStore.get(`messages/${id}/${archived}?page[number]=${page}`).then((res) => {
            for (let i = 0; i < messages.value.length; i++) {
                if (messages.value[i]._jv.id == id) {
                    messages.value[i]['unread_count'] = 0;
                    messages.value[i]['read'] = true;
                    break;
                }
            }

            const sortedRes = sortToPreservedJson(res);
            thread.value = thread.value.concat(sortedRes);
            if (sortedRes.length < 1) {
                threadMessagesPage = -1;
            }

            return res;
        });
    }

    function resetThread() {
        threadMessagesPage = 1;
        threadId.value = 0;
        thread.value = [];
    }

    function archiveMessage(id, archived = true) {
        if (!id) {
            return;
        }

        const data = {
            _jv: { id: id, type: 'messages' },
            id: id,
            archived: archived
        };

        return store.patch(data).then(() => {
            if (archived) {
                const index = messages.value.findIndex((message) => {
                    return message._jv.id === id;
                });
                const toArchiveMessage = messages.value[index];
                toArchiveMessage.archived = true;
                archivedMessages.value.push(toArchiveMessage);
                archivedMessages.value.sort((a, b) => b.date - a.date);
                
                messages.value.splice(index, 1);

                if (!messages.value.length) {
                    return false;
                }

                return index < messages.value.length 
                    ? messages.value[index]
                    : messages.value[0];
            } else {
                const index = archivedMessages.value.findIndex((message) => {
                    return message._jv.id === id;
                });
                const toMessage = archivedMessages.value[index];
                toMessage.archived = false;
                messages.value.push(toMessage);
                messages.value.sort((a, b) => b.date - a.date);

                archivedMessages.value.splice(index, 1);

                if (!archivedMessages.value.length) {
                    return false;
                }

                return index < archivedMessages.value.length 
                    ? archivedMessages.value[index] 
                    : archivedMessages.value[0];
            }
        });
    }

    // @TODO update
    function readMessage(id) {
        if (!id) {
            return;
        }

        const data = { _jv: { id: id, type: 'messages' }, id: id, read: true };

        return store.patch(data).then(() => {
            if (id in messages) {
                messages[id].read = true;
                messages[id].unread_count = 0;
            } else if (id in archivedMessages) {
                archivedMessages[id].read = true;
                archivedMessages[id].unread_count = 0;
            }
        });
    }

    function sendMessage(params = {}) {
        const newMessage = { ...{
            _jv: {
                type: 'messages',
                id: 0
            },
        }, ...params };

        return store.post(newMessage).then((data) => {
            // @see httpdocs/js/supplier.js
            Object.keys(params?.products || {}).forEach(productId => {
                legacyApi.post(`/supplier_catalog/event/inquire/${productId}`);
            });

            return data;
        });
    }

    function addSignature(signature) {
        const newSignature = {
            _jv: {
                type: 'member_signatures',
                id: 0
            },
            signature
        };
        return store.post(newSignature);
    }

    function updateSignature(id, signature) {
        const updateSignature = {
            _jv: {
                type: 'member_signatures',
                id
            },
            signature
        };
        return store.patch([updateSignature, { url: `member_signatures/${id}` }]);
    }

    function saveSignature(signature, id = null) {
        let promise;
        if (id) {
            promise = updateSignature(id, signature);
        } else {
            promise = addSignature(signature);

        }
        return promise.then((res) => {
            if (!userStore.user.signature?.id) {
                userStore.user.signature = {};
            }
            userStore.user.signature.id = res._jv.id;
            userStore.user.signature.signature = res.signature;
            userStore.bumpSession();
            return res;
        });
    }

    return {
        messages, 
        archivedMessages, 
        thread, 
        threadId,
        initialMessagesLoaded,
        initialArchivedMessagesLoaded,
        hasMessages, 
        hasConversations, 
        hasArchive, 
        from, 
        datedMessages, 
        hasThread, 
        isArchived,
        signatureDefault,
        getMessages, 
        messagesAreReady,
        messagesAreLoading,
        getArchivedMessages, 
        archivedMessagesAreReady,
        archivedMessagesAreLoading,
        getThread, 
        threadIsReady,
        threadIsLoading,
        archiveMessage, 
        sendMessage,
        readMessage, 
        archiveMessage, 
        resetThread,
        saveSignature
    }
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useInboxStore, import.meta.hot));
}
